'use client';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { websitePublicConfig } from '@/config/website-public';
import { useCreditBalance } from '@/hooks/credits/use-credits';
import { cn } from '@/lib/ui/utils';
import styles from './cms-remaining-credits-slot.module.css';

type CmsRemainingCreditsSlotCommonProps = {
  label: string;
  sidebarExpanded?: boolean;
  previewState?: {
    authenticated: boolean;
    availableCredits: number | null;
  };
};

type CmsRemainingCreditsSlotProps = CmsRemainingCreditsSlotCommonProps &
  (
    | {
        variant: 'account' | 'account-menu';
        onOpenCredits?: never;
      }
    | {
        variant: 'header';
        onOpenCredits: () => void;
      }
  );

function formatCredits(value: number | null) {
  if (value === null || Number.isNaN(value)) {
    return '—';
  }

  return value.toLocaleString();
}

function CmsRemainingCreditsDisplay(props: {
  variant: CmsRemainingCreditsSlotProps['variant'];
  label: string;
  onOpenCredits?: () => void;
  sidebarExpanded?: boolean;
  value: number | null;
  isLoading?: boolean;
}) {
  const accessibleLabel = props.isLoading
    ? props.label
    : `${props.label}: ${formatCredits(props.value)}`;
  const showIcon =
    props.isLoading || props.variant !== 'account' || props.sidebarExpanded;
  const className = cn(
    props.variant === 'header'
      ? styles.headerSlot
      : props.variant === 'account'
        ? styles.accountSlot
        : styles.accountMenuSlot,
    props.variant === 'account' &&
      !props.sidebarExpanded &&
      styles.accountSlotCollapsed
  );
  const content = (
    <>
      {showIcon ? (
        <span className={styles.iconWrap}>
          {props.isLoading
            ? renderAppIcon({
                name: 'loader2',
                className: `${styles.icon} ${styles.spinning}`,
              })
            : renderAppIcon({ name: 'coins', className: styles.icon })}
        </span>
      ) : null}
      <span>
        {props.isLoading ? null : (
          <span className={styles.value}>{formatCredits(props.value)}</span>
        )}
      </span>
    </>
  );

  if (props.variant === 'account' || props.variant === 'account-menu') {
    return (
      <span
        className={className}
        aria-label={accessibleLabel}
        aria-busy={props.isLoading || undefined}
        data-credits-variant={props.variant}
        title={props.label}
      >
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-label={accessibleLabel}
      aria-busy={props.isLoading || undefined}
      data-credits-variant={props.variant}
      onClick={props.onOpenCredits}
      title={props.label}
    >
      {content}
    </button>
  );
}

export function CmsRemainingCreditsSlot(props: CmsRemainingCreditsSlotProps) {
  if (props.previewState) {
    if (!props.previewState.authenticated) {
      return null;
    }

    return (
      <CmsRemainingCreditsDisplay
        variant={props.variant}
        label={props.label}
        onOpenCredits={props.onOpenCredits}
        sidebarExpanded={props.sidebarExpanded}
        value={props.previewState.availableCredits}
      />
    );
  }

  return <LiveCmsRemainingCreditsSlot {...props} />;
}

function LiveCmsRemainingCreditsSlot(props: CmsRemainingCreditsSlotProps) {
  const { data, isLoading } = useCreditBalance({
    enabled: websitePublicConfig.credits.enableCredits,
  });

  if (!websitePublicConfig.credits.enableCredits) {
    return null;
  }

  return (
    <CmsRemainingCreditsDisplay
      variant={props.variant}
      label={props.label}
      onOpenCredits={props.onOpenCredits}
      sidebarExpanded={props.sidebarExpanded}
      value={data?.balance ?? null}
      isLoading={isLoading}
    />
  );
}
